import _ from "lodash";
import React, { useState, useMemo } from "react";
import classNames from "classnames";
import { connect } from "react-redux";
import { Avatar } from "@toddle-edu/ds-web";
import { CaretDownFilled } from "@toddle-edu/ds-icons";

import { compose, graphql } from "lib/apolloClient";
import ButtonDropdown from "UIComponents/ButtonDropdown";
import I18nHOC from "UIComponents/I18nHOC";
import CurriculumProgramLogo from "UIComponents/CurriculumProgramLogo";
import ACLStore from "lib/aclStore";
import { goToRelativeRoute } from "modules/Services";
import { getChildrenFromCache } from "modules/CommonGraphqlHelpers";
import {
  switchChild,
  updateInvitedFamilyChildData,
  handleAcademicYearForParent,
  getUserInfo,
} from "Login/modules/LoginModule";
import { setAcademicYearSelected } from "Platform/modules/PlatformModule";
import { getChildrenQuery } from "modules/CommonQuery";
import {
  getFormattedUserName,
  getUserIdentifierName,
} from "Utils/GetFormattedUserName";
import {
  getOrganizationId,
  getUserId,
  getChildId,
  getIsChildBlocked,
  getChildProfileImage,
  getOrganizationName,
  getCurrentAcademicYear,
} from "modules/CommonSelectors";
import { markStudentRequestStateSeen } from "Courses/modules/CoursesModule";
import { checkIsDateAfter } from "Utils/DateTimeHelpers";
import { FAMILY_INVITE_STATUS_INVITED } from "Constants/stringConstants";

import ChildConnectionSuccess from "../ChildConnectionSuccess";
import AddChildModal from "../AddChildModal";
import ChildSelector from "../ChildSelector";

import styles from "../ParentHomePageStyles";
import classes from "./ChildDropDown.scss";

const childNameContainerClasses = classNames(
  classes.childNameContainer,
  "group"
);

const ChildDropDown = props => {
  const {
    organizationName,
    childID,
    isChildBlocked,
    childProfileImage,
    childFormattedName,
    allChildren,
    userCurriculumPrograms,
    currentCurriculumProgramType,
    currentCurriculumProgramAcronym,
    currentCurriculumProgramId,
    renderChildBox, // this is to render the child box component
    goToRelativeRoute,
    invitedFamilyChildData,
    userAcademicYears,
    setAcademicYearSelected,
    selectedAcademicYear,
    updateInvitedFamilyChildData,
    markStudentRequestStateSeen,
    t,
  } = props;

  // When teacher approves the class invite for a parent,
  // isRequestAccepted value will be set to true for the user
  // When parent logs in, if the value is true, it'll show the `Access granted for child A`
  // modal.
  const allChildrenPendingRequests = useMemo(
    () =>
      _.map(
        _.filter(allChildren, child => _.get(child, "isRequestAccepted")),
        child => ({ ...child, type: "class_invite" })
      ),
    [allChildren]
  );

  const [addChildModal, setAddChildModal] = useState(false);
  const [successModal, setSuccessModal] = useState(
    !_.isEmpty(invitedFamilyChildData) || _.size(allChildrenPendingRequests) > 0
  );
  const [childDetails, setChildDetails] = useState(
    !_.isEmpty(invitedFamilyChildData)
      ? invitedFamilyChildData
      : _.size(allChildrenPendingRequests) > 0
      ? allChildrenPendingRequests[0]
      : {}
  );
  const [pendingRequestedChildren, setPendingRequestedChildren] = useState(
    allChildrenPendingRequests
  );

  const onChildChangeClick = ({ id, reload = false }) => {
    const { userId } = props;
    const childrenFromCache = getChildrenFromCache({
      id: userId,
      type: "FAMILY_MEMBER",
    });

    const allChildren = _.get(childrenFromCache, "children", []);
    const childData = _.find(allChildren, { id: id });
    handleChildData({ childData, reload });
  };

  // this function will be called while switching child or adding child
  const handleChildData = ({ childData, reload }) => {
    const { switchChild, handleAcademicYearForParent } = props;
    // switch child
    switchChild({ childData });

    handleAcademicYearForParent();

    // do routing
    goToRelativeRoute({
      route: "/",
    });

    if (reload) {
      location.reload();
    }
  };

  const onAddChildClick = () => {
    setAddChildModal(true);
  };

  //  onClick functions
  const onDoneClick = () => {
    handleChildData({ childData: childDetails });
  };

  const onDoneChildSuccessModal = () => {
    let remainingPendingRequestedChildren = pendingRequestedChildren;

    if (!_.isEmpty(invitedFamilyChildData)) {
      // The current success modal is of family invite by email type
      updateInvitedFamilyChildData({});
      onDoneClick();
    } else {
      // current success modal is of family class invite type
      markStudentRequestStateSeen([_.get(childDetails, "id")]);
      remainingPendingRequestedChildren = _.slice(pendingRequestedChildren, 1);
    }

    if (_.size(remainingPendingRequestedChildren) === 0) {
      // No more children remaining for showing success modal
      setSuccessModal(false);
      setChildDetails({});
    } else {
      setChildDetails(remainingPendingRequestedChildren[0]);
    }

    onDoneClick();
    setPendingRequestedChildren(remainingPendingRequestedChildren);
  };

  const componentTestId = "homepage-childDropdown";

  return (
    <>
      <div className={classes.childDropdown} data-test-id={componentTestId}>
        <ButtonDropdown
          buttonComponent={
            <ButtonComponent
              childProfileImage={childProfileImage}
              childName={childFormattedName}
              organizationName={organizationName}
              userCurriculumPrograms={userCurriculumPrograms}
              currentCurriculumProgramType={currentCurriculumProgramType}
              currentCurriculumProgramAcronym={currentCurriculumProgramAcronym}
              componentTestId={componentTestId}
            />
          }
          containerStyle={styles.buttonDropDownContainerStyle}
          dropdownComponent={
            ACLStore.can("FeatureFlag:FamilyCurriculumSwitcher") ? (
              <ChildSelector
                allChildren={allChildren}
                childID={childID}
                isChildBlocked={isChildBlocked}
                onChildChangeClick={onChildChangeClick}
                onAddChildClick={onAddChildClick}
                addChildText={t("common:add_with_label", {
                  label: t("common:child"),
                })}
                userAcademicYears={userAcademicYears}
                setAcademicYearSelected={setAcademicYearSelected}
                selectedAcademicYear={selectedAcademicYear}
                t={t}
                goToRelativeRoute={goToRelativeRoute}
                childProfileImage={childProfileImage}
                childName={childFormattedName}
                organizationName={organizationName}
                userCurriculumPrograms={userCurriculumPrograms}
                currentCurriculumProgramId={currentCurriculumProgramId}
              />
            ) : (
              renderChildBox()
            )
          }
        />
      </div>
      {addChildModal ? (
        // add child modal
        <AddChildModal
          setAddChildModal={setAddChildModal}
          setSuccessModal={setSuccessModal}
          setChildDetails={setChildDetails}
          includePlatformUserQuery={true}
        />
      ) : null}
      {successModal ? (
        // success modal
        <ChildConnectionSuccess
          onClose={onDoneChildSuccessModal}
          studentName={getUserIdentifierName({ user: childDetails })}
          profileImage={_.get(childDetails, "profileImage")}
        />
      ) : null}
    </>
  );
};

// dropdown button component
const ButtonComponent = ({
  childProfileImage,
  childName,
  organizationName,
  userCurriculumPrograms,
  currentCurriculumProgramType,
  currentCurriculumProgramAcronym,
  componentTestId,
}) => {
  return (
    <div
      className={classes.buttonComponentContainer}
      data-test-id={componentTestId ? `${componentTestId}-button` : undefined}
    >
      <div className={classes.profileImage}>
        <Avatar
          shade={"light"}
          iconType={"individual"}
          size={"medium"}
          name={childName}
          src={childProfileImage}
        />
      </div>
      <div>
        <div className={childNameContainerClasses}>
          <div className={classes.childNameText}>{childName}</div>
          <CaretDownFilled size="xx-small" />
        </div>
        <div className={classes.orgNameRow}>
          <div className={classes.orgNameText}>{organizationName}</div>
          {(ACLStore.can("FeatureFlag:FamilyCurriculumSwitcher") &&
            _.size(userCurriculumPrograms)) > 1 ? (
            <>
              <span className={classes.dividerDot} />
              <CurriculumProgramLogo
                type={currentCurriculumProgramType}
                acronym={currentCurriculumProgramAcronym}
                height="24px"
              />
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  const userInfo = getUserInfo();
  const organizationId = getOrganizationId();
  const userId = getUserId();
  const childID = getChildId();
  const childProfileImage = getChildProfileImage();
  const organizationName = getOrganizationName();
  const isChildBlocked = getIsChildBlocked();
  const {
    childFirstName = "",
    childLastName = "",
    childMiddleName = "",
    childPreferredName = "",
    childPronouns = "",
    childPrefix = "",
    childSuffix = "",
  } = userInfo;
  const childFormattedName = getFormattedUserName({
    user: {
      firstName: childFirstName,
      lastName: childLastName,
      middleName: childMiddleName,
      preferredName: childPreferredName,
      pronouns: childPronouns,
      prefix: childPrefix,
      suffix: childSuffix,
    },
    type: "student",
  });

  const invitedFamilyChildData = _.get(
    state,
    "login.invitedFamilyChildData",
    {}
  );

  const currentCurriculumProgramType = _.get(
    state,
    "platform.currentCurriculumProgram.type",
    ""
  );
  const currentCurriculumProgramAcronym = _.get(
    state,
    "platform.currentCurriculumProgram.acronym",
    currentCurriculumProgramType
  );

  const currentCurriculumProgramId = _.get(
    state,
    "platform.currentCurriculumProgram.id",
    null
  );

  const productUpdateModalId = _.get(state, "login.productUpdateModalId");
  const shouldProductUpdatesModalOpen = _.get(
    state,
    "login.shouldProductUpdatesModalOpen"
  );

  const currentAcademicYear = getCurrentAcademicYear({ organizationId });
  const selectedAcademicYear = _.get(
    state,
    "platform.academicYearSelected",
    currentAcademicYear
  );

  const userDataFromCache = getChildrenFromCache({
    id: userId,
    type: _.get(userInfo, "userEntityType"),
  });

  const selectedStudentData = _.find(_.get(userDataFromCache, "children", []), {
    id: childID,
  });

  const userAcademicYears = _.get(selectedStudentData, "academicYears", []);

  const filteredAcademicYears = _.filter(userAcademicYears, year => {
    const { isCurrentAcademicYear, endDate } = year;

    if (isCurrentAcademicYear) return true;
    return checkIsDateAfter({
      date1: _.get(currentAcademicYear, "endDate"),
      date2: endDate,
      key: "year",
    });
  });

  return {
    userId,
    childID,
    organizationName,
    childProfileImage,
    isChildBlocked,
    childFormattedName,
    currentCurriculumProgramType,
    currentCurriculumProgramAcronym,
    currentCurriculumProgramId,
    invitedFamilyChildData,
    productUpdateModalId,
    shouldProductUpdatesModalOpen,
    userAcademicYears: filteredAcademicYears,
    selectedAcademicYear,
  };
};

const mapActionCreators = {
  goToRelativeRoute,
  handleAcademicYearForParent,
  switchChild,
  updateInvitedFamilyChildData,
  markStudentRequestStateSeen,
  setAcademicYearSelected,
};

export default compose(
  connect(mapStateToProps, mapActionCreators),
  I18nHOC({
    resource: ["common", "teacherHomePage", "announcement", "progressReport"],
  }),
  graphql(getChildrenQuery, {
    name: "getChildren",
    options: ({ userId }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: userId,
        type: "FAMILY_MEMBER",
      },
      context: { useHttpLink: true },
    }),
    props({ getChildren, ownProps: { userId } }) {
      const childrenFromCache = getChildrenFromCache({
        id: userId,
        type: "FAMILY_MEMBER",
      });

      const allChildren = _.get(childrenFromCache, "children", []);
      const children = _.filter(
        allChildren,
        childInfo =>
          _.get(childInfo, "parentStatus") !== FAMILY_INVITE_STATUS_INVITED
      );

      return {
        isData: !_.isEmpty(childrenFromCache),
        isLoading:
          getChildren["networkStatus"] == 1 ||
          getChildren["networkStatus"] == 2,
        allChildren: children,
      };
    },
  })
)(ChildDropDown);
